import React, { useCallback, useEffect, useRef, useState } from "react";
import "./RightUpdatesPanel.css";
import { FaBell, FaSyncAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const DEFAULT_WIDTH = 300;

// REAL ENDPOINTS
const LATEST_URL = "https://jkumarapi.axiomos.in/latest-activity";
const PROJECT_OVERVIEW_URL =
  "https://jkumarapi.axiomos.in/latest-activity/count";

const VIEW_MODES = {
  LATEST: "latest",
  OVERVIEW: "overview",
};

// Range keys must match backend keys
const RANGE_KEYS = {
  TODAY: "today",
  YESTERDAY: "yesterday",
  WEEK: "week",
  MONTH: "month",
  YEAR: "year",
};

const RANGE_LABELS = {
  [RANGE_KEYS.TODAY]: "Today",
  [RANGE_KEYS.YESTERDAY]: "Yesterday",
  [RANGE_KEYS.WEEK]: "This Week",
  [RANGE_KEYS.MONTH]: "Last 30 Days",
  [RANGE_KEYS.YEAR]: "This Year",
};

// Optional label mapping for modules
const MODULE_LABELS = {
  induction: "Induction",
  uauc: "UAUC",
  specific: "Specific Meeting",
  goodpractices: "Good Practices",
  workpermit: "Work Permit",
  meeting: "TBT / Meeting",
  workpermitdaily: "Work Permit Daily",
  incident: "Incident",
  inspection: "Inspection",
  violation: "Violation",
};

// Map API model → route segment (adjust if needed)
const MODEL_ROUTE_MAP = {
  uauc: "uauc",
  induction: "safetyinduction",
  meeting: "tbtmeeting",
  incident: "incident",
  inspection: "inspection",
  specific: "specific",
  goodpractices: "goodpractices",
  violation: "violation",
  workpermit: "workpermit",
  workpermitdaily: "workpermitdaily",
};

// Helper to build description line for "Latest"
const getUpdateDescription = (u) => {
  if (u.observation) return u.observation;
  if (u.violationDetails) return u.violationDetails;
  if (u.description) return u.description;

  if (u.model === "meeting") {
    return `TBT / Meeting – Attendees: ${u.attendeesNos || "-"}`;
  }

  if (u.model === "induction") {
    return "Induction session recorded.";
  }

  return u.project || "New record created.";
};

const RightUpdatesPanel = () => {
  const [viewMode, setViewMode] = useState(VIEW_MODES.LATEST);

  const [latestUpdates, setLatestUpdates] = useState([]);
  const [projectOverview, setProjectOverview] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const mountedRef = useRef(false);
  const requestInFlightRef = useRef(false);

  // Which range to show in Project Overview
  const [overviewRange, setOverviewRange] = useState(RANGE_KEYS.TODAY);

  const [width] = useState(() => {
    return (
      parseInt(localStorage.getItem("rightPanelWidth"), 10) || DEFAULT_WIDTH
    );
  });

  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem("rightPanelOpen");
    return saved ? saved === "true" : false;
  });

  const navigate = useNavigate();

  // The ref locks immediately, preventing overlapping requests from rapid clicks
  // and the development StrictMode effect replay on this component instance.
  const fetchAll = useCallback(async () => {
    if (requestInFlightRef.current) return;
    requestInFlightRef.current = true;
    setIsLoading(true);
    setFetchError("");

    const fetchJson = async (url) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    };

    try {
      const [latestResult, overviewResult] = await Promise.allSettled([
        fetchJson(LATEST_URL),
        fetchJson(PROJECT_OVERVIEW_URL),
      ]);

      if (!mountedRef.current) return;

      const errors = [];
      if (latestResult.status === "fulfilled") {
        const data = latestResult.value;
        setLatestUpdates(
          (Array.isArray(data?.latest100prioritized) &&
            data.latest100prioritized) ||
            (Array.isArray(data?.allRecords) && data.allRecords) ||
            [],
        );
      } else {
        errors.push("Latest activity could not be refreshed.");
      }

      if (overviewResult.status === "fulfilled") {
        const data = overviewResult.value;
        setProjectOverview(Array.isArray(data?.projects) ? data.projects : []);
      } else {
        errors.push("Project overview could not be refreshed.");
      }

      // Keep previously loaded data when its endpoint fails.
      setFetchError(errors.join(" "));
    } finally {
      requestInFlightRef.current = false;
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  // Load once per mount. Subsequent requests require the Refresh button.
  // No polling, tab-change requests, or range-change requests.
  useEffect(() => {
    mountedRef.current = true;
    void fetchAll();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchAll]);

  /* ========== EXPOSE WIDTH TO LAYOUT ========== */
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--right-panel-width",
      isOpen ? `${width}px` : "0px",
    );
    localStorage.setItem("rightPanelOpen", isOpen ? "true" : "false");
  }, [isOpen, width]);

  // On click of an update-item in "Latest" → /edit/{model}/{id}
  const handleItemClick = (item) => {
    if (!item) return;

    const modelKey = item.model;
    const routeSegment = MODEL_ROUTE_MAP[modelKey] || modelKey;

    const id = item.recordId || item.reportId;
    if (!routeSegment || !id) return;

    navigate(`/edit/${routeSegment}/${id}`);
  };

  /* ========== CLOSED MODE BUTTON ONLY ========== */
  if (!isOpen) {
    return (
      <button
        className="right-panel-toggle closed"
        onClick={() => setIsOpen(true)}
      >
        <FaBell size={18} />
      </button>
    );
  }

  // Decide what to render based on active tab
  const updates =
    viewMode === VIEW_MODES.LATEST ? latestUpdates : projectOverview;

  const rangeKey = overviewRange; // 'today' | 'yesterday' | 'week' | 'month' | 'year'

  return (
    <div className="right-panel" style={{ width }}>
      <div className="right-panel-header">
        {/* Top row: tabs + close */}
        <div className="header-top">
          <div className="right-panel-switch">
            <button
              className={
                "switch-btn" + (viewMode === VIEW_MODES.LATEST ? " active" : "")
              }
              onClick={() => setViewMode(VIEW_MODES.LATEST)}
            >
              Latest
            </button>
            <button
              className={
                "switch-btn" +
                (viewMode === VIEW_MODES.OVERVIEW ? " active" : "")
              }
              onClick={() => setViewMode(VIEW_MODES.OVERVIEW)}
            >
              Overview
            </button>
          </div>

          <button
            className="right-panel-close"
            onClick={() => setIsOpen(false)}
          >
            <span className="close-icon">×</span>
          </button>
        </div>

        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}
        >
          <button
            type="button"
            className="switch-btn right-panel-refresh"
            onClick={fetchAll}
            disabled={isLoading}
            aria-label="Refresh latest activity and project overview"
            aria-busy={isLoading}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              cursor: isLoading ? "wait" : "pointer",
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            <FaSyncAlt aria-hidden="true" />
            {isLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {fetchError && (
          <p role="alert" className="empty-msg">
            {fetchError} Click Refresh to try again.
          </p>
        )}

        {/* Range filter only in Overview */}
        {viewMode === VIEW_MODES.OVERVIEW && (
          <div className="right-panel-range-switch">
            {Object.entries(RANGE_LABELS).map(([key, label]) => (
              <button
                key={key}
                className={
                  "range-btn" + (overviewRange === key ? " active" : "")
                }
                onClick={() => setOverviewRange(key)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="updates-list" aria-busy={isLoading}>
        {updates.length === 0 ? (
          <p className="empty-msg">
            {isLoading
              ? "Loading updates…"
              : fetchError
                ? "Refresh to retry loading any unavailable data."
                : viewMode === VIEW_MODES.LATEST
                  ? "No recent activity in the last 14 days…"
                  : "No project overview data yet…"}
          </p>
        ) : viewMode === VIEW_MODES.LATEST ? (
          // ===== LATEST VIEW RENDER (using latest-activity payload) =====
          updates.map((u, i) => (
            <div
              key={i}
              className="update-item"
              onClick={() => handleItemClick(u)}
            >
              <p className="update-title">
                {u.model ? u.model.toUpperCase() : "UPDATE"}
                {u.reportId && (
                  <span className="update-subtitle"> · {u.reportId}</span>
                )}
              </p>

              <p className="update-desc">{getUpdateDescription(u)}</p>

              <span className="update-meta">
                {/* Project chip */}
                {u.project && <span className="chip">{u.project}</span>}

                {/* Risk rating chip for UAUC */}
                {u.riskRating && (
                  <span
                    className={`chip risk-${u.riskRating
                      ?.toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    {u.riskRating}
                  </span>
                )}

                {/* Status chip for UAUC / Violation etc. */}
                {u.status && (
                  <span
                    className={`chip status-${u.status
                      ?.toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    {u.status}
                  </span>
                )}

                {/* Attendees chip for meetings */}
                {u.model === "meeting" && u.attendeesNos && (
                  <span className="chip chip-soft">
                    {u.attendeesNos} attendees
                  </span>
                )}
              </span>

              <div className="update-footer">
                {u.createdBy && (
                  <span className="update-author">By {u.createdBy}</span>
                )}
                <span className="update-time">{u.submittedAt}</span>
              </div>
            </div>
          ))
        ) : (
          // ===== PROJECT OVERVIEW VIEW RENDER =====
          updates.map((p, i) => {
            const modules = p.modules || {};

            // Build per-module counts for selected range
            const moduleEntries = Object.entries(modules)
              .map(([modKey, counts]) => ({
                modKey,
                count: counts?.[rangeKey] || 0,
              }))
              .filter((m) => m.count > 0);

            // Total submissions for this project in selected range
            const totalForRange = moduleEntries.reduce(
              (sum, m) => sum + m.count,
              0,
            );

            return (
              <div key={p.projectId || i} className="update-item overview-item">
                <p className="update-title">
                  {p.projectName || p.projectId || "Project"}
                </p>

                <p className="update-desc">
                  {RANGE_LABELS[rangeKey]} submissions:{" "}
                  <strong>{totalForRange}</strong>
                </p>

                {/* Per-module chips */}
                <div className="overview-modules">
                  {moduleEntries.map(({ modKey, count }) => (
                    <span key={modKey} className="chip chip-soft">
                      {MODULE_LABELS[modKey] || modKey}:{" "}
                      <strong>{count}</strong>
                    </span>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RightUpdatesPanel;
